
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, AlertTriangle, DollarSign, Users, TrendingUp, UserPlus } from 'lucide-react';
import { useUserPointsSummary } from '@/hooks/points/useUserPointsSummary';
import { useCountriesForAnalytics } from '@/hooks/points/useCountriesForAnalytics';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const UserPointsAnalytics: React.FC = () => {
  const [searchEmail, setSearchEmail] = useState('');
  const [countryFilter, setCountryFilter] = useState('ALL_COUNTRIES');
  const [searchTerm, setSearchTerm] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);

  const { data, isLoading, error, refetch } = useUserPointsSummary(
    searchTerm,
    countryFilter === 'ALL_COUNTRIES' ? '' : countryFilter,
    50,
    0
  );

  const { countries, isLoading: countriesLoading } = useCountriesForAnalytics();

  const handleSearch = () => {
    setSearchTerm(searchEmail);
  };

  const handleClearSearch = () => {
    setSearchEmail('');
    setSearchTerm('');
    setCountryFilter('ALL_COUNTRIES');
  };

  const initializeMissingPointsRecords = async () => {
    setIsInitializing(true);
    try {
      // Get all verified users from user_country_verifications
      const { data: verifiedUsers, error: userError } = await supabase
        .from('user_country_verifications')
        .select('user_id, country_code')
        .eq('verification_status', 'verified');

      if (userError) throw userError;

      if (!verifiedUsers || verifiedUsers.length === 0) {
        toast.info('No verified users found');
        return;
      }

      // Get existing user_points records
      const { data: existingPoints, error: pointsError } = await supabase
        .from('user_points')
        .select('user_id, country_code');

      if (pointsError) throw pointsError;

      // Create a set of existing combinations for quick lookup
      const existingCombinations = new Set(
        (existingPoints || []).map(p => `${p.user_id}-${p.country_code}`)
      );

      // Find missing combinations
      const missingRecords = verifiedUsers
        .filter(user => !existingCombinations.has(`${user.user_id}-${user.country_code}`))
        .map(user => ({
          user_id: user.user_id,
          country_code: user.country_code,
          total_points: 0,
          lifetime_earned: 0
        }));

      if (missingRecords.length > 0) {
        const { error: insertError } = await supabase
          .from('user_points')
          .insert(missingRecords);

        if (insertError) throw insertError;
        
        toast.success(`Initialized points records for ${missingRecords.length} verified users`);
        refetch();
      } else {
        toast.info('All verified users already have points records');
      }
    } catch (error) {
      console.error('Error initializing missing points records:', error);
      toast.error('Failed to initialize missing points records');
    } finally {
      setIsInitializing(false);
    }
  };

  // Calculate summary statistics
  const totalUsers = data.length;
  const totalPointsInCirculation = data.reduce((sum, user) => sum + Number(user.total_points), 0);
  const suspiciousUsers = data.filter(user => user.suspicious_activity).length;
  const averagePoints = totalUsers > 0 ? totalPointsInCirculation / totalUsers : 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUsers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Points in Circulation</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalPointsInCirculation.toFixed(2)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Points</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averagePoints.toFixed(2)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Suspicious Activity</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{suspiciousUsers}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            User Points Search
            <Button
              onClick={initializeMissingPointsRecords}
              disabled={isInitializing}
              variant="outline"
              size="sm"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {isInitializing ? 'Initializing...' : 'Initialize Missing Records'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Input
                placeholder="Search by email address"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="w-48">
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL_COUNTRIES">All countries</SelectItem>
                  {countries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name} ({country.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSearch} disabled={isLoading}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearSearch}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Points Overview</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading user points data...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : data.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No users found. Try initializing missing records if you expect to see verified users here.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Total Points</TableHead>
                  <TableHead>Lifetime Earned</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{user.name || 'No name'}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>{user.country_code}</TableCell>
                    <TableCell className="font-medium">{Number(user.total_points).toFixed(2)}</TableCell>
                    <TableCell>{Number(user.lifetime_earned).toFixed(2)}</TableCell>
                    <TableCell>
                      {user.last_transaction_at 
                        ? formatDistanceToNow(new Date(user.last_transaction_at), { addSuffix: true })
                        : 'No activity'
                      }
                    </TableCell>
                    <TableCell>
                      {user.suspicious_activity ? (
                        <Badge variant="destructive">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Suspicious
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Normal</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserPointsAnalytics;
